import React from 'react';
import { render, useState, useEffect, useRef } from '@wordpress/element';
import useCareerSearchQuery, { useFieldsDebounce, CareerSearchResultEntry } from './hooks/useCareerSearchQuery';
import { useQueryClient, QueryClientProvider } from "react-query";

import './styles.scss';

async function fetchSearch(keywords: string, maxEducationDuration: number, minCareerSalary: number) {
	const url = new URL('https://' + location.hostname  + '/wp-json/wi-health-careers-search/v1/search');
	url.searchParams.append('keywords', keywords.toString());
	url.searchParams.append('maxEducationDuration', maxEducationDuration.toString());
	url.searchParams.append('minCareerSalary', minCareerSalary.toString());
	return (await fetch(url, {
		method: 'GET',
		credentials: 'same-origin',
	})).json();
}

document.addEventListener('DOMContentLoaded', function (event) {
	let counter = 0;
	const maxMaxEducationDuration = 20;
	const currency = Intl && Intl.NumberFormat ? new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 0}).format : (number: number | bigint) => '$' + number;

	function toMaybeRangeString(min, max, toString: ((value: any) => string) | null = null): string {
		if (typeof toString !== 'function')
			toString = value => value.toString();
		if (typeof max === 'undefined' || max === null || isNaN(max))
			return toString(min) + '+';
		if (max > min)
			return toString(min) + ' – ' + toString(max);
		return toString(min);
	}

	function CareerSearchResult(result: CareerSearchResultEntry) {
		return (<a className="career-search-result" href={result.url}>
			{result.title && <header>{result.title}</header>}
			{!!result.minEducation && result.minEducation > 0 && <div><i className="fas fa-school"></i> {toMaybeRangeString(result.minEducation, result?.maxEducation)} {result.educationUnit?.toLowerCase() || 'years'} of education</div>}
			{typeof result.minSalary === 'number' && <div><i className="fas fa-briefcase"></i> {toMaybeRangeString(result.minSalary, result?.maxSalary, currency)} salary</div>}
			{result.region && <div><i className="fas fa-map"></i> {result.region.join(', ')}</div>}
		</a>);
	}

	function CareerSearchResults(props: {
		results: CareerSearchResultEntry[]
	}) {
		return (<div className="career-search-results">{
			props.results.length
				? <><h3>Results</h3>{props.results.map(CareerSearchResult)}</>
				: <>No Results</>
		}</div>);
	}

	function CareerSearch() {
		const keywordsId = 'career-search-keywords-' + counter;
		const minCareerSalaryId = 'career-search-min-career-salary-' + counter;
		const maxEducationDurationId = 'career-search-max-edu-duration-' + counter;
		const [keywords, setKeywords] = useState(() => '');
		const [maxEducationDuration, setMaxEducationDuration] = useState(() => NaN);
		const [minCareerSalary, setMinCareerSalary] = useState(() => 10000);
		const [debouncedKeywords, debouncedMaxEducationDuration, debouncedMinCareerSalary] = useFieldsDebounce(keywords, maxEducationDuration, minCareerSalary);
		//const { isLoading, isError, data: results, error } = useCareerSearchQuery(keywords, maxEducationDuration, minCareerSalary);
		const [results, setResults] = useState([]);
		useEffect(() => {
			(async () => {
				setResults(await fetchSearch(debouncedKeywords, debouncedMaxEducationDuration, debouncedMinCareerSalary));
			})();
		}, [debouncedKeywords, debouncedMaxEducationDuration, debouncedMinCareerSalary]);
		return (<>
			<form className="career-search-form">
				<div>
					<label htmlFor={keywordsId}>Keywords: </label>
					<input id={keywordsId} type="text" value={keywords} onChange={e => setKeywords(e.target.value)} />
				</div>
				<div>
					<label htmlFor={minCareerSalaryId}>Minimum career salary: </label>
					<input id={minCareerSalaryId} type="number" value={minCareerSalary} min={0} max={1000000} step={1000}
						onChange={e => setMinCareerSalary(parseInt(e.target.value))} />
				</div>
				<div>
					<label htmlFor={maxEducationDurationId}>Maximum number of years of education: </label>
					<input id={maxEducationDurationId} type="number" value={maxEducationDuration} min={0} max={maxMaxEducationDuration}
						onChange={e => setMaxEducationDuration(parseInt(e.target.value))} />
				</div>
			</form>
			<CareerSearchResults results={results} />
		</>);
	}

	for (const node of document.querySelectorAll('.career-search') || [ document.getElementById('career-search') ]) {
		if (!node)
			continue;
		++counter;
		render(<CareerSearch/>, node);
	}
});
